// Path assumption: placed at Pages/Outreach/Compensation/IssueCompensationPage.tsx,
// same nesting depth as Pages/Outreach/Templates/TemplateFormPage.tsx (which is
// where the MailHtmlFrame / ViewItem relative import depths below are copied from) —
// adjust if you place it somewhere else.
import { ChangeEvent, ReactNode } from "react";
import { ViewItem, viewItemProps } from "../../../Components/Views/ViewItem";
import { MailHtmlFrame, sanitizeEmailHtml } from "../../../Components/Views/MailHtmlFrame";
import { controllers } from "../../../Controllers/Controllers";
import { BonusType, CompensationPreviewType, CompensationType } from "../../../DataTypes/compensation";
import { WorkerUserType } from "../../../DataTypes/activity";
// import '../outreach-styles.css'
// import '../Categories/categories-styles.css'
// import '../Templates/template-form-styles.css'
import './compensation-styles.css'

const todayISO = () => new Date().toISOString().slice(0, 10)

const firstDayOfMonthISO = () => {
    const d = new Date()
    d.setDate(1)
    return d.toISOString().slice(0, 10)
}

const formatMoney = (value: string | number) => `$${Number(value).toFixed(2)}`

type IssueCompensationPageProps = viewItemProps & {
    workerId?: number;
    onDone?: () => void;
}

type IssueCompensationPageState = {
    workers: WorkerUserType[];
    selectedWorkerId: string;
    periodStart: string;
    periodEnd: string;
    compensationDate: string;
    notes: string;

    preview: CompensationPreviewType | null;
    bonuses: BonusType[];
    invoiceHtml: string;
    totalAmount: string;
    previewLoading: boolean;
    previewError: string | null;

    newBonusTitle: string;
    newBonusDescription: string;
    newBonusAmount: string;

    issuing: boolean;
    issueError: string | null;
    issuedCompensation: CompensationType | null;
}

class IssueCompensationPage extends ViewItem {
    props: IssueCompensationPageProps;
    state: IssueCompensationPageState;

    constructor(props: IssueCompensationPageProps) {
        super(props)
        this.props = props
        this.state = {
            workers: [],
            selectedWorkerId: props.workerId ? String(props.workerId) : '',
            periodStart: firstDayOfMonthISO(),
            periodEnd: todayISO(),
            compensationDate: todayISO(),
            notes: '',

            preview: null,
            bonuses: [],
            invoiceHtml: '',
            totalAmount: '0.00',
            previewLoading: false,
            previewError: null,

            newBonusTitle: '',
            newBonusDescription: '',
            newBonusAmount: '',

            issuing: false,
            issueError: null,
            issuedCompensation: null,
        }
    }

    componentDidMount(): void {
        this.loadWorkers()
        if (this.state.selectedWorkerId) this.loadPreview()
    }

    loadWorkers = async () => {
        // Reuses the same worker list already used by MyDashboardPage's
        // admin "view as" selector — no need for a second endpoint.
        const response = await controllers().outreach.getWorkers()
        if (response && response.statusCode === 200) {
            this.setState({ workers: response.data?.workers || [] })
        }
    }

    handleWorkerChange = (e: ChangeEvent<HTMLSelectElement>) => {
        this.setState({ selectedWorkerId: e.target.value, preview: null, bonuses: [], invoiceHtml: '', issuedCompensation: null })
    }

    handleDateField = (field: 'periodStart' | 'periodEnd' | 'compensationDate') => (e: ChangeEvent<HTMLInputElement>) => {
        this.setState({ [field]: e.target.value } as any)
    }

    handleNotesChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        this.setState({ notes: e.target.value })
    }

    // Pulls fresh numbers from the server every time (the contacted-lead
    // count is always recomputed there — never trust a stale client
    // copy of it). Pass `bonusesOverride` to render the invoice with an
    // exact draft list (after adding/removing a bonus); omit it on the
    // very first load for a worker/period so the server can seed the
    // draft with the auto bonus itself.
    loadPreview = async (bonusesOverride?: BonusType[]) => {
        const { selectedWorkerId, periodStart, periodEnd, compensationDate, notes } = this.state
        if (!selectedWorkerId || !periodStart || !periodEnd) return

        this.setState({ previewLoading: true, previewError: null })
        const response = await controllers().compensation.getCompensationPreview({
            worker_id: Number(selectedWorkerId),
            period_start: periodStart,
            period_end: periodEnd,
            compensation_date: compensationDate,
            notes,
            bonuses: bonusesOverride,
        })

        if (!response || response.statusCode !== 200) {
            this.setState({ previewLoading: false, previewError: response?.error || 'Failed to load preview.' })
            return
        }

        const preview: CompensationPreviewType = response.data
        const nextBonuses = bonusesOverride !== undefined
            ? bonusesOverride
            : (preview.auto_bonus.contacted_count > 0 ? [preview.auto_bonus] : [])

        this.restoreAutoBonus()
        this.setState({
            previewLoading: false,
            preview,
            bonuses: nextBonuses,
            invoiceHtml: preview.invoice_html_preview || '',
            totalAmount: preview.total_amount || preview.salary_amount,
        })
    }

    handleLoadPreviewClick = () => {
        this.loadPreview(this.state.preview ? this.state.bonuses : undefined)
    }

    removeBonus = (index: number) => {
        const nextBonuses = this.state.bonuses.filter((_, i) => i !== index)
        this.setState({ bonuses: nextBonuses })
        this.loadPreview(nextBonuses)
    }

    restoreAutoBonus = () => {
        const { preview, bonuses } = this.state
        if (!preview || bonuses.some(b => b.bonus_type === 'contacted_leads')) return
        const nextBonuses = [preview.auto_bonus, ...bonuses]
        this.setState({ bonuses: nextBonuses })
        this.loadPreview(nextBonuses)
    }

    handleAddManualBonus = () => {
        const { newBonusTitle, newBonusAmount, newBonusDescription, bonuses } = this.state
        const amountNum = Number(newBonusAmount)
        if (!newBonusTitle.trim() || !newBonusAmount || Number.isNaN(amountNum)) {
            this.setState({ previewError: 'Give the bonus a title and a valid amount.' })
            return
        }
        const nextBonuses: BonusType[] = [
            ...bonuses,
            { bonus_type: 'manual', title: newBonusTitle.trim(), description: newBonusDescription.trim(), amount: amountNum.toFixed(2) },
        ]
        this.setState({ bonuses: nextBonuses, newBonusTitle: '', newBonusDescription: '', newBonusAmount: '', previewError: null })
        this.loadPreview(nextBonuses)
    }

    handleIssue = async () => {
        const { selectedWorkerId, periodStart, periodEnd, compensationDate, notes, bonuses, preview } = this.state
        if (!selectedWorkerId || !periodStart || !periodEnd || !preview) {
            this.setState({ issueError: 'Load a preview before issuing.' })
            return
        }

        this.setState({ issuing: true, issueError: null })
        const response = await controllers().compensation.issueCompensation({
            worker_id: Number(selectedWorkerId),
            period_start: periodStart,
            period_end: periodEnd,
            compensation_date: compensationDate,
            notes,
            bonuses,
        })

        if (!response || response.statusCode !== 200) {
            this.setState({ issuing: false, issueError: response?.error || 'Failed to issue compensation.' })
            return
        }

        this.setState({ issuing: false, issuedCompensation: response.data })
    }

    resetForAnother = () => {
        this.setState({
            issuedCompensation: null, preview: null, bonuses: [], invoiceHtml: '',
            notes: '', issueError: null,
        })
        this.props.onDone?.()
    }

    renderBonusEditor = () => {
        const { preview, bonuses, newBonusTitle, newBonusDescription, newBonusAmount, issuedCompensation } = this.state
        if (!preview) return null
        const hasAutoBonus = bonuses.some(b => b.bonus_type === 'contacted_leads')
        const disabled = !!issuedCompensation

        return (
            <div className="lead-form-section">
                <h3 className="lead-form-section-title">Bonuses</h3>

                {!hasAutoBonus && preview.auto_bonus.contacted_count > 0 && (
                    <div className="outreach-banner outreach-banner-info comp-auto-bonus-banner">
                        <span className="outreach-banner-text">
                            Contacted-leads bonus removed ({preview.auto_bonus.contacted_count} contacted, would be {formatMoney(preview.auto_bonus.amount)}).
                        </span>
                        {!disabled && <button className="outreach-banner-action" onClick={this.restoreAutoBonus}>Restore</button>}
                    </div>
                )}

                {bonuses.length > 0 && (
                    <div className="comp-bonus-list">
                        {bonuses.map((b, i) => (
                            <div key={i} className="comp-bonus-row">
                                <div className="comp-bonus-info">
                                    <span className="comp-bonus-title">
                                        {b.title}
                                        {b.bonus_type === 'contacted_leads' && (
                                            <span className="comp-bonus-badge">auto · {b.contacted_count} contacted</span>
                                        )}
                                    </span>
                                    {b.description && <span className="comp-bonus-desc">{b.description}</span>}
                                </div>
                                <span className="comp-bonus-amount">{formatMoney(b.amount)}</span>
                                {!disabled && (
                                    <button className="comp-bonus-remove" onClick={() => this.removeBonus(i)} title="Remove bonus">✕</button>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {!disabled && (
                    <div className="comp-add-bonus-form">
                        <input
                            className="category-form-input"
                            placeholder="Bonus title"
                            value={newBonusTitle}
                            onChange={(e) => this.setState({ newBonusTitle: e.target.value })}
                        />
                        <input
                            className="category-form-input"
                            placeholder="Description (optional)"
                            value={newBonusDescription}
                            onChange={(e) => this.setState({ newBonusDescription: e.target.value })}
                        />
                        <input
                            className="category-form-input comp-add-bonus-amount"
                            type="number" step="0.01" min={0}
                            placeholder="Amount"
                            value={newBonusAmount}
                            onChange={(e) => this.setState({ newBonusAmount: e.target.value })}
                        />
                        <button className="outreach-add-btn" onClick={this.handleAddManualBonus}>Add bonus</button>
                    </div>
                )}
            </div>
        )
    }

    renderPreviewPanel = () => {
        const { preview, invoiceHtml, previewLoading, previewError, totalAmount, issuedCompensation } = this.state

        return (
            <div className="lead-form-section template-preview-panel comp-invoice-preview-panel">
                <h3 className="lead-form-section-title">Invoice preview</h3>

                {!preview && !previewLoading && (
                    <p className="outreach-muted template-preview-empty">
                        Pick a worker and period, then "Load preview" to see the invoice before issuing.
                    </p>
                )}

                {previewLoading && <div className="outreach-state"><div className="outreach-spinner" /></div>}

                {!previewLoading && previewError && (
                    <div className="outreach-banner outreach-banner-error">
                        <span className="outreach-banner-text">{previewError}</span>
                    </div>
                )}

                {!previewLoading && preview && (
                    <>
                        {preview.has_overlapping_compensation && !issuedCompensation && (
                            <div className="outreach-banner outreach-banner-info">
                                <span className="outreach-banner-text">
                                    This worker already has a compensation covering part of this period.
                                </span>
                            </div>
                        )}
                        <div className="comp-preview-total-row">
                            <span>Total to issue</span>
                            <strong>{formatMoney(totalAmount)}</strong>
                        </div>
                        {invoiceHtml && (
                            <div className="settings-signature-preview">
                                <MailHtmlFrame html={sanitizeEmailHtml(invoiceHtml)} />
                            </div>
                        )}
                    </>
                )}

                {issuedCompensation && (
                    <div className="outreach-banner outreach-banner-info comp-issued-banner">
                        <span className="outreach-banner-text">
                            Compensation issued — total {formatMoney(issuedCompensation.total_amount)}.
                        </span>
                        {issuedCompensation.invoice_url && (
                            <a className="outreach-banner-action" href={issuedCompensation.invoice_url} target="_blank" rel="noreferrer">
                                View invoice
                            </a>
                        )}
                    </div>
                )}
            </div>
        )
    }

    render(): ReactNode {
        const {
            workers, selectedWorkerId, periodStart, periodEnd, compensationDate, notes,
            previewLoading, issuing, issueError, issuedCompensation,
        } = this.state

        return (
            <div className="outreach-page">
                <div className="outreach-header">
                    <div className="outreach-title-header">
                        <h1 className="outreach-title">Issue compensation</h1>
                        <p className="outreach-subtitle">All compensations are issued manually — nothing here runs on a schedule.</p>
                    </div>
                </div>

                {issueError && <div className="category-form-error">{issueError}</div>}

                <div className="template-form-columns">
                    <div className="template-form-main-col">
                        <div className="lead-form-section">
                            <h3 className="lead-form-section-title">Worker &amp; period</h3>
                            <div className="lead-form-grid">
                                <div className="lead-form-field lead-form-field-wide">
                                    <label className="category-form-label">Worker</label>
                                    <select
                                        className="category-form-input"
                                        value={selectedWorkerId}
                                        onChange={this.handleWorkerChange}
                                        disabled={!!issuedCompensation}
                                    >
                                        <option value="">Select a worker...</option>
                                        {workers.map(w => (
                                            <option key={w.id} value={w.id}>{w.username || w.email}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="lead-form-field">
                                    <label className="category-form-label">Period start</label>
                                    <input type="date" className="category-form-input" value={periodStart}
                                        onChange={this.handleDateField('periodStart')} disabled={!!issuedCompensation} />
                                </div>
                                <div className="lead-form-field">
                                    <label className="category-form-label">Period end</label>
                                    <input type="date" className="category-form-input" value={periodEnd}
                                        onChange={this.handleDateField('periodEnd')} disabled={!!issuedCompensation} />
                                </div>
                                <div className="lead-form-field">
                                    <label className="category-form-label">Compensation date</label>
                                    <input type="date" className="category-form-input" value={compensationDate}
                                        onChange={this.handleDateField('compensationDate')} disabled={!!issuedCompensation} />
                                </div>
                            </div>
                            <div className="category-form-group">
                                <label className="category-form-label">Notes (optional, shown on the invoice)</label>
                                <textarea
                                    className="category-form-textarea"
                                    rows={3}
                                    value={notes}
                                    onChange={this.handleNotesChange}
                                    disabled={!!issuedCompensation}
                                />
                            </div>
                            <div className="lead-form-actions comp-load-preview-actions">
                                <button
                                    className="outreach-add-btn"
                                    onClick={this.handleLoadPreviewClick}
                                    disabled={!selectedWorkerId || previewLoading || !!issuedCompensation}
                                >
                                    {previewLoading ? 'Loading...' : 'Load preview'}
                                </button>
                                {issuedCompensation ? (
                                <button className="outreach-add-btn" onClick={this.resetForAnother}>
                                    Issue another
                                </button>
                            ) : (
                                <button
                                    className="outreach-add-btn lead-form-compose-btn"
                                    onClick={this.handleIssue}
                                    disabled={issuing || !this.state.preview}
                                >
                                    {issuing ? 'Issuing...' : 'Issue compensation'}
                                </button>
                            )}
                            </div>

                        </div>

                        {this.renderBonusEditor()}

                        {/* <div className="lead-form-actions">
                            
                        </div> */}
                    </div>

                    <div className="template-form-preview-col">
                        {this.renderPreviewPanel()}
                    </div>
                </div>
            </div>
        )
    }
}

export { IssueCompensationPage }
